import React, { Component } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import MaterialIcon from 'material-icons-react';
import IconCircle from 'admin/components/IconCircle';
import { trackEvent } from 'admin/services/tracking/GtmTracking';
import { Button, colors, media, ThemeProvider, defaultTheme } from '@material-dev/thread';
import { Flex, Box } from '@rebass/grid';
import OtherThingsButton from './OtherThingsButton';

const Container = styled(Flex)`
  width: 100%;
  padding: 0 24px;
  color: ${colors.white};

  ${media.sm`
    padding: 0 80px;
    max-width: 800px;
  `}
`;

const StyledIcon = styled(IconCircle).attrs({
  backgroundColor: colors.white
})`
  display: none;
  margin-bottom: 28px;
  justify-content: center;
  align-items: center;

  ${media.sm`
    display: flex;
  `}
`;

const SmallStyledIcon = styled(StyledIcon).attrs({
  size: '48px'
})`
  margin: 16px 0;
  display: flex;

  ${media.sm`
    display: none;
  `}
`;

const Headline = styled.h1`
  font-size: 24px;
  line-height: 32px;

  ${media.sm`
    font-size: 48px;
    line-height: 48px;
  `}
`;

const Subhead = styled.p`
  color: ${colors.white70};
`;

const ActionsContainer = styled(Flex).attrs({
  flexDirection: ['column', 'row'],
  justifyContent: 'flex-start',
  alignItems: 'center',
  flexWrap: 'wrap'
})`
  margin: 8px 0 14px;
  width: 100%;
`;

const SecondaryButton = styled(Button).attrs({
  large: true
})`
  border-color: ${colors.white30};
  color: ${colors.white};
  width: 100%;
  margin: 0;
  margin-bottom: 16px;

  ${media.sm`
    margin-right: 20px;
    width: auto;
  `}

  &:last-child {
    margin-right: 0;
  }

  i {
    color: ${colors.white} !important;
    margin-right: 8px;
  }

  * {
    /* prevent incorrect gtm triggering on button children */
    pointer-events: none;
  }
`;

const PrimaryButton = styled(SecondaryButton).attrs({
  variant: 'primary'
})`
  background-color: ${colors.white};
  box-shadow: none;
  color: ${props => props.color || colors.black87};
  margin-bottom: 16px;

  &:hover {
    background-color: ${colors.white70};
  }

  i {
    color: ${props => props.color || colors.black87} !important;
  }
`;

const OtherThingsHeader = styled.h3`
  text-transform: uppercase;
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 1px;
  line-height: 16px;
  margin-bottom: 16px;
  color: ${colors.white70};
`;

const HelpMessageContainer = styled(Flex).attrs({
  flexDirection: 'row',
  justifyContent: 'start',
  alignItems: 'start'
})`
  margin-top: 16px;
  margin-bottom: 32px;
  color: ${colors.white70};

  .material-icons {
    margin-right: 16px;
  }

  span {
    color: ${colors.white};
    font-weight: bold;
  }

  ${media.sm`
    margin-top: 48px;
    margin-bottom: 0;
  `}
`;

const TextLink = styled.a`
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
`;


class EmptyListPage extends Component {
  render() {
    return (
      <ThemeProvider theme={defaultTheme}>
        <Container flexDirection="column" justifyContent="center" alignItems="flex-start">
          {!!this.props.icon && (
            <React.Fragment>
              <StyledIcon>
                <MaterialIcon icon={this.props.icon} size={48} color={this.props.color} />
              </StyledIcon>

              <SmallStyledIcon>
                <MaterialIcon icon={this.props.icon} size={24} color={this.props.color} />
              </SmallStyledIcon>
            </React.Fragment>
          )}

          <Headline>{this.props.headline}</Headline>

          <Subhead>{this.props.message}</Subhead>

          <ActionsContainer>
            {
              this.props.onCreateClicked &&
                <PrimaryButton onClick={this.props.onCreateClicked}
                  id={`admin_${this.props.page}_new_record`}
                  icon="add" variant="primary"
                  color={this.props.color}>

                  {this.props.createLabel}
                </PrimaryButton>
            }

            {
              this.props.onVideoClicked &&
                <SecondaryButton onClick={this.props.onVideoClicked}
                  id={`admin_${this.props.page}_watch_video`}
                  icon="play_circle_outline"
                  variant="link">

                  Watch the Video
                </SecondaryButton>
            }
          </ActionsContainer>

          {!!this.props.ctas.length && (
            <Box width={1}>
              <OtherThingsHeader>Other Things to Try</OtherThingsHeader>

              <Flex flexDirection={['column', 'row']} justifyContent={['flex-start']}>
                {this.props.ctas.map((cta, index) => {
                  return (
                    <OtherThingsButton
                      key={index}
                      icon={cta.icon}
                      svgIcon={cta.svgIcon}
                      heading={cta.heading}
                      subhead={cta.subhead}
                      onClick={cta.action}
                      name={cta.name}/>
                  );
                })}
              </Flex>
            </Box>
          )}

          <HelpMessageContainer>
            <MaterialIcon icon="help_outline" size={26} color="inherit" />
            <div>
              <span>{this.props.helpText} </span>
              <TextLink onClick={this.openChat}>Chat online now</TextLink>.
            </div>
          </HelpMessageContainer>
        </Container>
      </ThemeProvider>
    );
  }

  openChat = () => {
    trackEvent('admin_onboarding_chatnow');
    window.Intercom('showNewMessage');
  }

  closeDemoModal = () => this.setState({ demoModalIsOpen: false });
}

EmptyListPage.propTypes = {
  page: PropTypes.string.isRequired,
  icon: PropTypes.string,
  color: PropTypes.string,
  headline: PropTypes.string.isRequired,
  message: PropTypes.string,
  onVideoClicked: PropTypes.func,
  ctas: PropTypes.array,
  onCreateClicked: PropTypes.func,
  createLabel: PropTypes.string,
  helpText: PropTypes.string.isRequired
};

EmptyListPage.defaultProps = {
  color: 'inherit',
  icon: '',
  message: null,
  onVideoClicked: null,
  onCreateClicked: null,
  createLabel: 'Add Item',
  ctas: []
};

export default EmptyListPage;


// WEBPACK FOOTER //
// ./src/app/admin/components/EmptyListPage2/index.js